import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
class LukePairPicker extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    try { this.messages = JSON.parse(this.querySelector('[data-pair-i18n]')?.textContent || '{}'); } catch { this.messages = {}; }
    this.selects = [...this.querySelectorAll('[data-pair-variant]')];
    this.button = this.querySelector('[data-pair-add]');
    this.status = this.querySelector('[data-pair-status]');
    this.addEventListener('change', () => this.update());
    this.button.addEventListener('click', () => this.add());
    this.update();
  }
  t(key) { return this.messages[key] || ''; }
  messageError(message) { const error = new Error(message); error.pairMessage = true; return error; }
  money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100);
  }
  update() {
    let total = 0;
    let complete = true;
    for (const select of this.selects) {
      const option = select.selectedOptions[0];
      const chosen = Boolean(select.value);
      if (!chosen) complete = false;
      const price = Number(option?.dataset.price || 0);
      total += price;
      const item = select.closest('.luke-pair__item');
      item.querySelector('[data-pair-price]').textContent = chosen ? this.money(price) : this.t('confirm_price');
      const link = item.querySelector('a');
      if (link && select.value) { const url = new URL(link.href, location.href); url.searchParams.set('variant', select.value); link.href = url.toString(); }
      const image = item.querySelector('img');
      if (image && option?.dataset.image) { image.removeAttribute('srcset'); image.src = option.dataset.image; }
    }
    this.querySelector('[data-pair-total]').textContent = complete ? this.money(total) : this.t('choose_both');
    this.button.disabled = this.busy || !complete;
  }
  async verifyAvailability(items) {
    const page = await fetch(location.href, { cache: 'no-store', headers: { Accept: 'text/html' } });
    if (!page.ok) throw this.messageError(this.t('availability_error'));
    const html = new DOMParser().parseFromString(await page.text(), 'text/html');
    const current = html.querySelector(`luke-pair-picker[data-product-id="${this.dataset.productId}"]`);
    const lists = current ? [...current.querySelectorAll('[data-pair-variant]')] : [];
    if (lists.length !== items.length || items.some((item, i) => ![...lists[i].options].some(option => !option.disabled && option.value === String(item.id)))) {
      throw this.messageError(this.t('unavailable'));
    }
  }
  async add() {
    if (this.busy) return;
    if (this.selects.some(select => !select.value)) {
      this.status.textContent = this.t('choose_each');
      this.update();
      return;
    }
    this.busy = true;
    this.button.disabled = true;
    this.button.textContent = this.t('adding');
    this.status.textContent = '';
    const items = this.selects.map(select => ({ id: Number(select.value), quantity: 1 }));
    const root = window.Shopify?.routes?.root || '/';
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map(el => el.dataset.sectionId).filter(Boolean))];
    let added = false;
    try {
      await this.verifyAvailability(items);
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections, sections_url: location.pathname }) });
      const data = await response.json();
      if (!response.ok || data.status) throw this.messageError(data.description || data.message || this.t('add_error'));
      added = true;
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw this.messageError(this.t('added_open'));
      const cart = await cartResponse.json();
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: items.map(item => ({ merchandiseId: String(item.id), quantity: 1 })), promise: deferred.promise }));
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'product-form-component', sourceId: `luke-pair-${this.dataset.productId}`, itemCount: 2, productId: this.dataset.productId, sections: data.sections, didError: false } });
      this.status.textContent = this.t('added_success');
    } catch (error) {
      this.status.textContent = added ? this.t('added_review') : (error.pairMessage && error.message ? error.message : this.t('update_error'));
      const link = document.createElement('a'); link.href = `${root}cart`; link.textContent = ' ' + this.t('view_bag'); this.status.append(link);
    } finally {
      this.busy = false;
      this.button.disabled = this.selects.some(select => !select.value);
      this.button.textContent = this.t('add_both');
    }
  }
}
if (!customElements.get('luke-pair-picker')) customElements.define('luke-pair-picker', LukePairPicker);

class LukePairRegion extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.closest('.shopify-section, dialog, product-card')?.addEventListener(StandardEvents.productSelect, async event => {
      const generation = this.generation = (this.generation || 0) + 1;
      try {
        const { detail } = await event.promise;
        if (!this.isConnected || generation !== this.generation || !detail?.html) return;
        if (detail.productId && String(detail.productId) !== this.dataset.productId) return;
        const next = detail.html.querySelector(`luke-pair-region[data-product-id="${this.dataset.productId}"]`);
        this.innerHTML = next?.innerHTML || '';
      } catch { /* Leave the last confirmed options; add preflight rechecks eligibility. */ }
    }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
}
if (!customElements.get('luke-pair-region')) customElements.define('luke-pair-region', LukePairRegion);


